import type { RouteObject } from 'react-router';
import { paths } from '@/config/paths';
import type { RouteHandle } from '@/shared/routing';
import { reportById } from './management/reportCatalog';

/** KPI portal report pages (prototype pages) and record forms, each loaded on demand. */
export const createReportRoutes = (): RouteObject[] => [
  {
    path: paths.overview,
    handle: { title: 'Overview' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./overview/OverviewPage')).OverviewPage },
  },
  {
    path: paths.trends,
    handle: { title: 'Trends' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./trends/TrendsPage')).TrendsPage },
  },
  {
    path: paths.revenue,
    handle: { title: 'Revenue' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./revenue/RevenuePage')).RevenuePage },
  },
  {
    path: paths.subscriptions,
    handle: { title: 'Subscriptions' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./lists/ListPage')).SubscriptionsPage },
  },
  {
    path: paths.subscriptionsDashboard,
    handle: { title: 'Subscriptions dashboard' } satisfies RouteHandle,
    lazy: {
      Component: async () =>
        (await import('./subscriptions/SubscriptionsDashboardPage')).SubscriptionsDashboardPage,
    },
  },
  {
    path: paths.subscriptionLifecycle,
    handle: { title: 'Subscription lifecycle' } satisfies RouteHandle,
    lazy: {
      Component: async () =>
        (await import('./subscriptions/SubscriptionLifecyclePage')).SubscriptionLifecyclePage,
    },
  },
  {
    path: paths.subscriptionChurn,
    handle: { title: 'Churn analysis' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./subscriptions/ChurnPage')).ChurnPage },
  },
  {
    path: paths.subscriptionComparison,
    handle: { title: 'Subscription comparison' } satisfies RouteHandle,
    lazy: {
      Component: async () =>
        (await import('./subscriptions/SubscriptionComparisonPage')).SubscriptionComparisonPage,
    },
  },
  {
    path: paths.subscriptionReports,
    handle: { title: 'Subscription reports' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./management/ReportsPage')).ReportsPage },
  },
  {
    path: paths.subscriptionReport(':reportId'),
    handle: {
      title: ({ reportId }) => reportById(reportId)?.title ?? 'Report not found',
    } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./management/ReportPage')).ReportPage },
  },
  {
    path: paths.subscriptionViews,
    handle: { title: 'Subscription data views' } satisfies RouteHandle,
    lazy: {
      Component: async () => (await import('./lists/DataViewsPage')).SubscriptionDataViewsPage,
    },
  },
  {
    path: paths.renewals,
    handle: { title: 'Renewals' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./renewals/RenewalsPage')).RenewalsPage },
  },
  {
    path: paths.offers,
    handle: { title: 'Product offers' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./offers/OffersPage')).OffersPage },
  },
  {
    path: paths.offerPrices('standard'),
    handle: { title: 'Standard offers' } satisfies RouteHandle,
    lazy: {
      Component: async () => (await import('./offers/OfferPricesPage')).StandardOffersPage,
    },
  },
  {
    path: paths.offerPrices('custom'),
    handle: { title: 'Custom offers' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./offers/OfferPricesPage')).CustomOffersPage },
  },
  {
    path: paths.offerPrices('archived'),
    handle: { title: 'Archived offers' } satisfies RouteHandle,
    lazy: {
      Component: async () => (await import('./offers/OfferPricesPage')).ArchivedOffersPage,
    },
  },
  {
    path: paths.discounts,
    handle: { title: 'Discounts' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./offers/PromotionPages')).DiscountsPage },
  },
  {
    path: paths.coupons,
    handle: { title: 'Coupons' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./offers/PromotionPages')).CouponsPage },
  },
  {
    path: paths.users,
    handle: { title: 'Users' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./lists/ListPage')).UsersPage },
  },
  {
    path: paths.userViews,
    handle: { title: 'User data views' } satisfies RouteHandle,
    lazy: {
      Component: async () => (await import('./lists/DataViewsPage')).UserDataViewsPage,
    },
  },
  {
    path: paths.apps,
    handle: { title: 'Apps' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./lists/ListPage')).AppsPage },
  },
  {
    path: paths.appsDashboard,
    handle: { title: 'Apps dashboard' } satisfies RouteHandle,
    lazy: {
      Component: async () => (await import('./apps/AppsDashboardPage')).AppsDashboardPage,
    },
  },
  {
    path: paths.appViews,
    handle: { title: 'App data views' } satisfies RouteHandle,
    lazy: {
      Component: async () => (await import('./lists/DataViewsPage')).AppDataViewsPage,
    },
  },
  {
    path: paths.cohorts,
    handle: { title: 'Cohorts' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./cohorts/CohortsPage')).CohortsPage },
  },
  {
    path: paths.totals,
    handle: { title: 'Totals' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./totals/TotalsPage')).TotalsPage },
  },
  {
    path: paths.conversion,
    handle: { title: 'Conversion' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./growth/ConversionPage')).ConversionPage },
  },
  {
    path: paths.trials,
    handle: { title: 'Trials' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./trials/TrialsPage')).TrialsPage },
  },
  {
    path: paths.markets,
    handle: { title: 'Markets' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./growth/MarketsPage')).MarketsPage },
  },
  {
    path: paths.leads,
    handle: { title: 'Leads' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./lists/ListPage')).LeadsPage },
  },
  {
    path: paths.leadViews,
    handle: { title: 'Lead data views' } satisfies RouteHandle,
    lazy: {
      Component: async () => (await import('./lists/DataViewsPage')).LeadDataViewsPage,
    },
  },
  {
    path: `${paths.subscriptions}/:id`,
    handle: { title: 'Subscription' } satisfies RouteHandle,
    lazy: {
      Component: async () =>
        (await import('./records/SubscriptionRecordPage')).SubscriptionRecordPage,
    },
  },
  {
    path: `${paths.users}/:id`,
    handle: { title: 'User' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./records/UserRecordPage')).UserRecordPage },
  },
  {
    path: `${paths.apps}/:id`,
    handle: { title: 'App' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./records/AppRecordPage')).AppRecordPage },
  },
  {
    path: paths.profile,
    handle: { title: 'Profile' } satisfies RouteHandle,
    lazy: { Component: async () => (await import('./profile/ProfilePage')).ProfilePage },
  },
];
